import { useState, useEffect } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { AlertTriangle, Bot, Plus, X } from 'lucide-react';

import { API } from '@/lib/api';
import { NutriFichaFields } from './NutriFichaFields';

// Estado inicial del formulario — incluye datos básicos + ficha nutricional.
const emptyForm = {
  nombre: '',
  apellido: '',
  email: '',
  telefono: '',
  fecha_nacimiento: '',
  direccion: '',
  sexo: '',
  servicio_bot: false,
  alertas_medicas: [],
  // nutriología
  peso: '',
  estatura: '',
  objetivo: '',
  enfermedades_cronicas: [],
  alergias_alimentarias: [],
  actividad_fisica: '',
  fuma: '',
  consume_alcohol: '',
  grupo_sanguineo: '',
  // ficha de identificación (nutrición)
  ocupacion: '',
  horas_fuera_casa: '',
  quien_prepara_alimentos: '',
  donde_compra_alimentos: '',
  alimentos_no_gusta: '',
  num_comidas_dia: '',
  diagnostico_nutricional: '',
  // embarazo / lactancia
  embarazo: '',
  meses_embarazo: '',
  peso_pre_embarazo: '',
  complicaciones_embarazo: '',
  lactancia: '',
  meses_lactancia: '',
};

// Convierte un paciente del backend al shape del formulario (strings vacíos en vez de null).
const patientToForm = (patient) => ({
  nombre: patient.nombre,
  apellido: patient.apellido,
  email: patient.email || '',
  telefono: patient.telefono,
  fecha_nacimiento: patient.fecha_nacimiento,
  direccion: patient.direccion || '',
  sexo: patient.sexo || '',
  servicio_bot: patient.servicio_bot ?? false,
  alertas_medicas: patient.alertas_medicas || [],
  peso: patient.peso ?? '',
  estatura: patient.estatura ?? '',
  objetivo: patient.objetivo || '',
  enfermedades_cronicas: patient.enfermedades_cronicas || [],
  alergias_alimentarias: patient.alergias_alimentarias || [],
  actividad_fisica: patient.actividad_fisica || '',
  fuma: patient.fuma == null ? '' : String(patient.fuma),
  consume_alcohol: patient.consume_alcohol || '',
  grupo_sanguineo: patient.grupo_sanguineo || '',
  ocupacion: patient.ocupacion || '',
  horas_fuera_casa: patient.horas_fuera_casa || '',
  quien_prepara_alimentos: patient.quien_prepara_alimentos || '',
  donde_compra_alimentos: patient.donde_compra_alimentos || '',
  alimentos_no_gusta: patient.alimentos_no_gusta || '',
  num_comidas_dia: patient.num_comidas_dia ?? '',
  diagnostico_nutricional: patient.diagnostico_nutricional || '',
  embarazo: patient.embarazo == null ? '' : String(patient.embarazo),
  meses_embarazo: patient.meses_embarazo ?? '',
  peso_pre_embarazo: patient.peso_pre_embarazo ?? '',
  complicaciones_embarazo: patient.complicaciones_embarazo || '',
  lactancia: patient.lactancia == null ? '' : String(patient.lactancia),
  meses_lactancia: patient.meses_lactancia ?? '',
});

// Construye el payload para el backend (strings vacíos → null, numéricos parseados).
const formToPayload = (formData) => ({
  ...formData,
  email: formData.email || null,
  direccion: formData.direccion || null,
  peso: formData.peso !== '' ? parseFloat(formData.peso) : null,
  estatura: formData.estatura !== '' ? parseFloat(formData.estatura) : null,
  objetivo: formData.objetivo || null,
  actividad_fisica: formData.actividad_fisica || null,
  fuma: formData.fuma !== '' ? formData.fuma === 'true' : null,
  consume_alcohol: formData.consume_alcohol || null,
  grupo_sanguineo: formData.grupo_sanguineo || null,
  sexo: formData.sexo || null,
  ocupacion: formData.ocupacion || null,
  horas_fuera_casa: formData.horas_fuera_casa || null,
  quien_prepara_alimentos: formData.quien_prepara_alimentos || null,
  donde_compra_alimentos: formData.donde_compra_alimentos || null,
  alimentos_no_gusta: formData.alimentos_no_gusta || null,
  num_comidas_dia: formData.num_comidas_dia !== '' ? parseInt(formData.num_comidas_dia) : null,
  diagnostico_nutricional: formData.diagnostico_nutricional || null,
  embarazo: formData.embarazo !== '' ? formData.embarazo === 'true' : null,
  meses_embarazo: formData.meses_embarazo !== '' ? parseInt(formData.meses_embarazo) : null,
  peso_pre_embarazo: formData.peso_pre_embarazo !== '' ? parseFloat(formData.peso_pre_embarazo) : null,
  complicaciones_embarazo: formData.complicaciones_embarazo || null,
  lactancia: formData.lactancia !== '' ? formData.lactancia === 'true' : null,
  meses_lactancia: formData.meses_lactancia !== '' ? parseInt(formData.meses_lactancia) : null,
});

/**
 * Diálogo de alta/edición de paciente. Único formulario reutilizado por la
 * lista de pacientes y la vista de detalle.
 *
 * Props:
 *  - open, onOpenChange: control del diálogo
 *  - patient: paciente a editar, o null para crear uno nuevo
 *  - onSaved: callback(pacienteGuardado) tras guardar con éxito
 */
export const PatientFormDialog = ({ open, onOpenChange, patient = null, onSaved }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [newAlerta, setNewAlerta] = useState({ tipo: '', descripcion: '' });
  const [showNutriSection, setShowNutriSection] = useState(false);

  // Al abrir, sincroniza el formulario con el paciente (o lo resetea para alta).
  useEffect(() => {
    if (!open) return;
    if (patient) {
      setFormData(patientToForm(patient));
      const hasNutri = patient.peso || patient.estatura || patient.objetivo ||
        patient.enfermedades_cronicas?.length || patient.alergias_alimentarias?.length;
      setShowNutriSection(!!hasNutri);
    } else {
      setFormData(emptyForm);
      setShowNutriSection(false);
    }
    setNewAlerta({ tipo: '', descripcion: '' });
  }, [open, patient]);

  const handleAddAlerta = () => {
    if (newAlerta.tipo && newAlerta.descripcion) {
      setFormData(prev => ({
        ...prev,
        alertas_medicas: [...prev.alertas_medicas, { ...newAlerta, severidad: 'alta' }],
      }));
      setNewAlerta({ tipo: '', descripcion: '' });
    }
  };

  const handleRemoveAlerta = (index) => {
    setFormData(prev => ({
      ...prev,
      alertas_medicas: prev.alertas_medicas.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre || !formData.apellido || !formData.telefono || !formData.fecha_nacimiento) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    const payload = formToPayload(formData);

    try {
      let saved;
      if (patient) {
        const res = await axios.put(`${API}/patients/${patient.id}`, payload);
        saved = res.data;
        toast.success('Paciente actualizado correctamente');
      } else {
        const res = await axios.post(`${API}/patients`, payload);
        saved = res.data;
        toast.success('Paciente agregado correctamente');
      }
      onSaved?.(saved);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg).join(', ')
        : (typeof detail === 'string' ? detail : 'Error al guardar paciente');
      toast.error(msg);
      return;
    }

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{patient ? 'Editar Paciente' : 'Nuevo Paciente'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                placeholder="Juan"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                data-testid="patient-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="apellido">Apellido *</Label>
              <Input
                id="apellido"
                placeholder="Pérez López"
                value={formData.apellido}
                onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                data-testid="patient-lastname-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="telefono">Teléfono *</Label>
            <Input
              id="telefono"
              placeholder="+52 555 123 4567"
              value={formData.telefono}
              onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
              data-testid="patient-phone-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Correo Electrónico</Label>
            <Input
              id="email"
              type="email"
              placeholder="paciente@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="patient-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fecha_nacimiento">Fecha de Nacimiento *</Label>
            <Input
              id="fecha_nacimiento"
              type="date"
              value={formData.fecha_nacimiento}
              onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
              data-testid="patient-birthdate-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Sexo</Label>
            <Select value={formData.sexo} onValueChange={v => setFormData({ ...formData, sexo: v })}>
              <SelectTrigger><SelectValue placeholder="No especificado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="masculino">Masculino</SelectItem>
                <SelectItem value="femenino">Femenino</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="direccion">Dirección</Label>
            <Textarea
              id="direccion"
              placeholder="Calle, Número, Colonia, Ciudad"
              value={formData.direccion}
              onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
              rows={2}
            />
          </div>

          {/* Servicio de bot */}
          <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-900">
            <div className="space-y-1">
              <Label htmlFor="servicio_bot" className="flex items-center gap-2 text-sky-700 dark:text-sky-400">
                <Bot className="h-4 w-4" />
                Servicio de bot
              </Label>
              <p className="text-xs text-muted-foreground">
                El paciente conserva acceso a su plan alimenticio por el bot aunque ya no asista a consulta.
              </p>
            </div>
            <Switch
              id="servicio_bot"
              checked={formData.servicio_bot}
              onCheckedChange={(v) => setFormData({ ...formData, servicio_bot: v })}
              data-testid="patient-servicio-bot-switch"
            />
          </div>

          {/* Alertas Médicas */}
          <div className="space-y-3 p-4 rounded-lg bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900">
            <Label className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
              <AlertTriangle className="h-4 w-4" />
              Alertas Médicas
            </Label>

            {formData.alertas_medicas.length > 0 && (
              <div className="space-y-2">
                {formData.alertas_medicas.map((alerta, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-white dark:bg-slate-900 p-2 rounded">
                    <Badge variant="destructive" className="text-xs">{alerta.tipo}</Badge>
                    <span className="text-sm flex-1">{alerta.descripcion}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleRemoveAlerta(idx)}
                      aria-label={`Quitar alerta ${alerta.tipo}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Input
                placeholder="Tipo (ej: Alergia)"
                value={newAlerta.tipo}
                onChange={(e) => setNewAlerta({ ...newAlerta, tipo: e.target.value })}
                className="w-1/3"
              />
              <Input
                placeholder="Descripción"
                value={newAlerta.descripcion}
                onChange={(e) => setNewAlerta({ ...newAlerta, descripcion: e.target.value })}
                className="flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={handleAddAlerta}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Sección Nutriológica (componente modular) */}
          <NutriFichaFields
            formData={formData}
            setFormData={setFormData}
            defaultOpen={showNutriSection}
          />

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1" data-testid="save-patient-btn">
              {patient ? 'Guardar Cambios' : 'Agregar Paciente'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
